import type { Polygon } from "geojson";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { StorageService } from "../../../../../comun/services/storage/StorageService";
import { RegistroFincaBorrador } from "../../../../../modulos/terceros/wizard/response/RegistroFincaBorrador";
import { parseWizardErrorMessage } from "../../../../../modulos/terceros/wizard/response/WizardValidationErrorResponse";
import { registroFincaTempGrpcService } from "../../../../../modulos/terceros/wizard/services/RegistroFincaTempGrpcService";
import { PoligonoConNombre } from "../map/depurarPoligonos";
import {
  BaseRegistroFincaPasoRender,
  RegistroFincaFooterOptions,
} from "./BaseRegistroFincaPasoRender";
import template from "./registroFincaPaso2Template.html";

/** Paso 2 del wizard de registro: elegir dibujar sobre el mapa vs cargar un
 *  archivo KML/KMZ. Nombre/tipo ya están en el registro (paso 1).
 *
 *  Los dos caminos avanzan distinto: "Dibujar" solo marca la elección y
 *  espera el click en "Continuar" (footer); "Cargar KML" avanza directo a
 *  paso 3 apenas el archivo se procesa bien.
 *
 *  **Qué se guarda por el camino "Dibujar".** Solo la `fuente` del registro
 *  temporal ("Dibujado sobre mapa"): el área la dibuja el usuario en el paso 3.
 *
 *  **Qué pasa por el camino "Cargar KML".**
 *  1. el archivo se sube a Azure Blob con un SAS de escritura
 *     (`StorageService.upload`: pide el SAS por gRPC-Web y hace el PUT directo
 *     al blob, igual que cualquier adjunto de la app);
 *  2. el SERVIDOR lo lee y resuelve todo el procesamiento
 *     (`importarArchivoKmlAsync`): parsea el KML/KMZ, abre los multipolígonos
 *     en polígonos independientes, toma el nombre del Placemark si lo trae,
 *     redondea a 7 decimales, depura (descarta inválidos, duplicados y
 *     solapados, y avisa qué descartó) y agrupa el resto en **polígonos
 *     padre**. En la misma llamada deja guardados la fuente, el archivo y los
 *     polígonos del archivo **como lotes** del registro;
 *  3. acá se guardan esos padres como el área del registro
 *     (`guardarAoiAsync`), y el servidor calcula con ellos el **perímetro
 *     global**: los une tendiendo puentes entre los bloques separados.
 *
 *  **Por qué todo el procesamiento está en el servidor.** El parseo del XML es
 *  sincrónico y congelaba la pantalla; la depuración compara todos los
 *  polígonos del archivo contra todos; y la unión con puentes tardaba segundos
 *  con un archivo grande. Nada de eso puede correr en el hilo que tiene que
 *  dibujar el paso siguiente.
 *
 *  **Nada viaja en memoria al paso 3.** Se avanza con la lista de importados
 *  VACÍA: cuando el paso 3 monta, los padres ya están en `poligonos_padre` y
 *  los lotes en `tbl_temp_lotes_finca`, así que los lee de la base y los pinta
 *  con su capa WMS, igual que al retomar un registro. Pasárselos acá los
 *  marcaría como "recién importados", que es la rama que el paso 3 reserva
 *  para piezas que todavía no existen en la base y por eso pinta con Leaflet.
 *  El import server-side cambió de dónde salen los polígonos, no cómo los
 *  consumen los pasos 3 a 6.
 *
 *  **Por qué el paso 3 muestra los padres y no los lotes del archivo.** El
 *  paso 3 es el del área del predio, no el de su subdivisión: mostrar ahí los
 *  40 lotes de un archivo confundía —parecía que había que editarlos uno por
 *  uno— cuando lo que se confirma en ese paso es el perímetro. Los lotes no se
 *  pierden: ya están guardados (punto 2) y el "Continuar" del paso 3 los
 *  conserva, porque `ConfirmarPiezas` ve que cubren más del 99% de su pieza
 *  padre y que esa pieza no se editó. Si el usuario SÍ edita un padre, sus
 *  lotes se descartan — que es justo lo que corresponde.
 *
 *  El paso se tapa con la capa "Importando archivo" (`_mostrarCargando`) desde
 *  que se elige el archivo hasta que se avanza o falla: cubre la subida, el
 *  procesamiento en el servidor y el guardado del área.
 *
 *  **Y el paso 3 la continúa.** Esta capa se va con el DOM del paso 2 al
 *  cambiar de paso, que es antes de que el mapa del paso 3 muestre nada: ahí
 *  todavía faltan leer el registro, montar el mapa, pedir el ticket y traer
 *  los tiles. El paso 3 vuelve a tapar el mapa con la misma capa hasta que el
 *  WMS pintó las piezas (`RegistroFincaPaso3Render._taparMapa`), así que el
 *  usuario ve un solo estado de carga continuo y el mapa aparece ya con el
 *  área dibujada. */
export class RegistroFincaPaso2Render extends BaseRegistroFincaPasoRender {
  private _pathChoice: "draw" | null = null;
  private _guardando = false;

  private _cardDraw: HTMLElement | null = null;
  private _cardKml: HTMLElement | null = null;
  private _kmlInput: HTMLInputElement | null = null;
  private _cargandoEl: HTMLElement | null = null;

  private readonly _storage = new StorageService();

  constructor(
    private readonly _borrador: RegistroFincaBorrador,
    private readonly _onVolver: () => void,
    private readonly _onContinuar: (importados: PoligonoConNombre[]) => void,
  ) {
    super(_borrador.clienteId);
  }

  renderBody(): string {
    return template;
  }

  connectBody(): void {
    this._cardDraw = document.getElementById("registroFincaChoiceDraw");
    this._cardKml = document.getElementById("registroFincaChoiceKml");
    this._kmlInput = document.getElementById(
      "registroFincaKmlInput",
    ) as HTMLInputElement | null;
    this._cargandoEl = document.getElementById("registroFincaPaso2Cargando");

    this._cardDraw?.addEventListener("click", this._onSelectDraw);
    this._cardKml?.addEventListener("click", this._onClickKmlCard);
    this._kmlInput?.addEventListener("change", this._onKmlFilePicked);
  }

  disposeBody(): void {
    this._cardDraw?.removeEventListener("click", this._onSelectDraw);
    this._cardKml?.removeEventListener("click", this._onClickKmlCard);
    this._kmlInput?.removeEventListener("change", this._onKmlFilePicked);
  }

  getFooterOptions(): RegistroFincaFooterOptions {
    return {
      volver: { onClick: this._onVolver },
      submit: {
        label: "Continuar →",
        disabled: true,
        onClick: () => {
          if (this._pathChoice !== "draw") return;
          this._guardarFuenteDibujoYContinuar();
        },
      },
    };
  }

  private _onSelectDraw = (): void => {
    this._pathChoice = "draw";
    this._cardDraw?.classList.add("selected");
    this._cardKml?.classList.remove("selected");
    const btnSubmit = this._btnSubmit();
    if (btnSubmit) btnSubmit.disabled = false;
  };

  private _onClickKmlCard = (): void => {
    if (this._guardando) return;
    this._kmlInput?.click();
  };

  private _onKmlFilePicked = (): void => {
    const file = this._kmlInput?.files?.[0];
    if (!file) return;
    // `_guardando` cubre desde que se elige el archivo: la subida y el
    // procesamiento son la parte lenta, y sin esto se podía elegir otro
    // archivo encima mientras el primero estaba en vuelo.
    if (this._guardando) return;
    this._guardando = true;
    this._mostrarCargando(true);

    this._importarArchivoAsync(file).catch((error) => {
      this._guardando = false;
      this._mostrarCargando(false);
      // Limpia el input: si el usuario reintenta con el MISMO archivo
      // (ej. lo corrigió y lo volvió a exportar con igual nombre), el
      // navegador solo dispara "change" de nuevo si el value cambió.
      if (this._kmlInput) this._kmlInput.value = "";
      serviceProvider
        .getErrorService()
        .addError(
          "Error al cargar el archivo KML",
          parseWizardErrorMessage(error),
        );
    });
  };

  /** Sube el archivo, deja que el servidor lo procese y avanza al paso 3 con
   *  los polígonos padre que devolvió. Lo que falle acá lo informa el `catch`
   *  de `_onKmlFilePicked`, salvo el guardado del área, que se avisa pero no
   *  impide avanzar (ver `_guardarPerimetroAsync`). */
  private async _importarArchivoAsync(file: File): Promise<void> {
    const registroId =
      await registroFincaTempGrpcService.asegurarRegistroAsync(this._borrador);

    const blobUrl = await this._storage.upload(file);

    const resultado =
      await registroFincaTempGrpcService.importarArchivoKmlAsync(
        registroId,
        blobUrl,
        file.name,
      );

    if (resultado.avisoDepuracion) {
      serviceProvider
        .getErrorService()
        .addWarning(
          "Polígonos descartados del archivo",
          resultado.avisoDepuracion,
        );
    }

    // El servidor ya guardó la fuente (y con ella descartó lo que quedara de
    // un dibujo o de un archivo anterior), así que el borrador se alinea sin
    // una llamada aparte a `guardarFuenteAsync`.
    this._borrador.aoiOrigen = "kml";
    this._borrador.ultimoPaso = Math.max(this._borrador.ultimoPaso, 2);

    await this._guardarPerimetroAsync(registroId, resultado.poligonosPadre);

    // La capa de carga NO se apaga acá: el paso 3 se monta enseguida y
    // apagarla antes dejaría el paso 2 clickeable un instante.
    this._guardando = false;
    // **Se avanza SIN polígonos en memoria, a propósito.** Los padres ya están
    // en `poligonos_padre` (los guardó `_guardarPerimetroAsync` recién), así
    // que el paso 3 los lee de la base y los pinta con su capa WMS, igual que
    // al retomar un registro. Pasárselos acá los marcaría como "recién
    // importados", que es la rama que el paso 3 reserva para piezas que
    // todavía NO existen en la base y por eso tiene que pintar con Leaflet
    // (ver `piezasEnLaBase` en `RegistroFincaPaso3Render`): el mapa quedaba
    // renderizado por el cliente en vez de por GeoServer. Lo que cambió con el
    // import server-side es de dónde salen los polígonos, no cómo los
    // consumen los pasos 3 a 6.
    this._onContinuar([]);
  }

  /** Guarda las piezas del archivo como el área del registro. El perímetro
   *  —la unión con puentes— lo calcula el servidor con esas piezas.
   *
   *  Si lo rechaza (ej. se solapa con otra finca) se avisa pero se sigue al
   *  paso 3 igual: ahí están los polígonos para corregirlo, y cada corrección
   *  vuelve a guardar. */
  private async _guardarPerimetroAsync(
    registroId: string,
    poligonosPadre: Polygon[],
  ): Promise<void> {
    try {
      const { areaCambio } = await registroFincaTempGrpcService.guardarAoiAsync(
        registroId,
        poligonosPadre,
      );
      this._borrador.tieneArea = true;
      if (areaCambio) {
        this._borrador.ultimoPaso = Math.min(this._borrador.ultimoPaso, 2);
      }
    } catch (error) {
      serviceProvider
        .getErrorService()
        .addError(
          "Error al guardar el área de la finca",
          parseWizardErrorMessage(error),
        );
    }
  }

  private _mostrarCargando(visible: boolean): void {
    if (this._cargandoEl) this._cargandoEl.hidden = !visible;
  }

  /** Camino "Dibujar": guarda la fuente en el registro temporal y avanza al
   *  paso 3 sin nada dibujado todavía. */
  private _guardarFuenteDibujoYContinuar(): void {
    if (this._guardando) return;
    this._guardando = true;
    const btnSubmit = this._btnSubmit();
    if (btnSubmit) btnSubmit.disabled = true;

    registroFincaTempGrpcService
      .asegurarRegistroAsync(this._borrador)
      .then((registroId) =>
        registroFincaTempGrpcService.guardarFuenteAsync(registroId, "draw"),
      )
      .then(({ datosDescartados }) => {
        this._borrador.aoiOrigen = "draw";
        if (datosDescartados) {
          // Venía de un archivo: el servidor descartó su área y sus lotes.
          this._borrador.tieneArea = false;
          this._borrador.ultimoPaso = 2;
        } else {
          this._borrador.ultimoPaso = Math.max(this._borrador.ultimoPaso, 2);
        }
        this._onContinuar([]);
      })
      .catch((error) => {
        this._guardando = false;
        if (btnSubmit) btnSubmit.disabled = this._pathChoice !== "draw";
        serviceProvider
          .getErrorService()
          .addError(
            "Error al guardar el registro",
            parseWizardErrorMessage(error),
          );
      });
  }

  private _btnSubmit(): HTMLButtonElement | null {
    return document.getElementById(
      "registroFincaSubmit",
    ) as HTMLButtonElement | null;
  }
}
